'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Box, TextField } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  type MRT_SortingState,
} from 'material-react-table';
import {
  queryInventoryAuditorLocationCatalog,
  type InventoryAuditorItemRow,
} from '@/hooks/useInventoryAuditorInventory';
import { useNotification } from '@/contexts/NotificationContext';

type LocationInventoryRow = InventoryAuditorItemRow & { quantityAtLocation: number };

/** Prefer UPC-A (12) when stored as zero-padded EAN-13. */
function displayUpc(raw: string | null | undefined): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '—';
  if (digits.length === 13 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

const SORTABLE = new Set(['upc', 'name', 'quantity', 'id']);

const InventoryAuditorLocationInventoryPanel: React.FC<{ locationId: number }> = ({ locationId }) => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'name', desc: false }]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<LocationInventoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const { showError } = useNotification();

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [debounced, locationId]);

  const orderBy = sorting[0]?.id && SORTABLE.has(sorting[0].id) ? sorting[0].id : 'name';
  const orderDirection = sorting[0]?.desc ? 'DESC' : 'ASC';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await queryInventoryAuditorLocationCatalog({
        locationId,
        search: debounced,
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        orderBy,
        orderDirection,
      });
      setRows(r.data);
      setTotal(r.total);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Failed to load inventory');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [locationId, debounced, pagination.pageIndex, pagination.pageSize, orderBy, orderDirection, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<MRT_ColumnDef<LocationInventoryRow>[]>(
    () => [
      {
        id: 'image',
        header: '',
        size: 52,
        enableSorting: false,
        Cell: ({ row }) => (
          <Avatar variant="rounded" src={row.original.imageUrl || undefined} sx={{ width: 36, height: 36 }}>
            <Inventory2Icon fontSize="small" />
          </Avatar>
        ),
      },
      {
        accessorKey: 'upc',
        header: 'UPC',
        size: 140,
        Cell: ({ cell }) => displayUpc(cell.getValue<string>()),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        size: 260,
        Cell: ({ cell }) => cell.getValue<string | null>()?.trim() || '—',
      },
      {
        accessorKey: 'uomPackType',
        header: 'UOM',
        size: 90,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
      },
      {
        accessorKey: 'size',
        header: 'Size',
        size: 90,
        enableSorting: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
      },
      {
        id: 'quantity',
        accessorKey: 'quantityAtLocation',
        header: 'Qty',
        size: 80,
        Cell: ({ cell }) => cell.getValue<number>(),
      },
    ],
    [],
  );

  return (
    <Box>
      <MaterialReactTable
        columns={columns}
        data={rows}
        rowCount={total}
        manualPagination
        manualSorting
        enableGlobalFilter={false}
        enableColumnFilters={false}
        enableDensityToggle
        enableFullScreenToggle
        enableHiding
        enableColumnResizing
        enableStickyHeader
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        initialState={{ density: 'compact' }}
        state={{ isLoading: loading, pagination, sorting }}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 320px)' } }}
        renderTopToolbarCustomActions={() => (
          <TextField
            size="small"
            placeholder="Search UPC / name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
          />
        )}
      />
    </Box>
  );
};

export default InventoryAuditorLocationInventoryPanel;
